import React from 'react'

import { components } from 'react-select'

import { isSsoSaml } from 'utils/auth'
import { LOGIN_PROVIDER } from 'definitions'
import { useStores } from 'stores'
import { validateEmail } from 'utils/validators'
import { useDebounce } from 'hooks/useDebounce'
import Input from 'common/Forms/Input'
import NormalInput from 'common/Input'
import ReactSelect from 'common/Forms/ReactSelect'
import PlaceholderOption from 'common/PlaceholderOption'
import ShowMoreButton from 'common/ShowMoreButton'

export default ({ resource, client, session, punchoutSession, disabled,languages }) => {
    const { clientAreaStore } = useStores()
    const [inputValue, setInputValue] = React.useState('')
    const [options, setOptions] = React.useState([])
    const [page, setPage] = React.useState(1)
    const [loadingMore, setLoadingMore] = React.useState(false)
    const [menuIsOpen, setMenuIsOpen] = React.useState(undefined)
    const [hasSelected, setHasSelected] = React.useState(false)
    const pageSize = 50

    const loadOptions = React.useCallback(
        async (search, page) => {
            setLoadingMore(true)
            const response = await clientAreaStore.getClientSites({ search, pageSize, page })
            setLoadingMore(false)

            if (page === 1) {
                setOptions(response.data)
            } else {
                setOptions(prev => [...prev, ...response.data])
            }

            // Restore focus after re-render (fix for Edge)
            const input = document.querySelector('[id="$user.clientSite"] input')
            if (input) {
                input.focus()
            }
        },
        [clientAreaStore, pageSize]
    )

    React.useEffect(() => {
        loadOptions(inputValue, page)
    }, [inputValue, page, loadOptions])

    const debouncedSetInputValue = useDebounce(value => {
        setInputValue(value)
        setPage(1)
        setHasSelected(false)
    }, 300)

    const sites = React.useMemo(() => {
        const result = Array.isArray(options) ? [...options] : []
        const totalLoaded = result.length
        const hasMore = totalLoaded > 0 && totalLoaded === pageSize * page

        if (hasMore) {
            result.push({ isLoadMore: true, label: resource.ShowMore })
        }

        return result
    }, [options, page, pageSize, resource])


    const defaultSite = React.useMemo(() => {
        if (punchoutSession.siteId) {
            return sites.find(item => item.value === punchoutSession.siteId)
        }
        return null
    }, [sites, punchoutSession])

    const handleInputChange = (newValue, actionMeta) => {
        // Only handle user input, not other events
        if (actionMeta.action === 'input-change') {
            debouncedSetInputValue(newValue)
            return newValue
        }
        return newValue
    }

    const handleChange = () => {
        // Clear the input value after selecting an option
        setInputValue('')
        setPage(1)
        setHasSelected(true)
        setMenuIsOpen(false)
    }

    const handleMenuOpen = () => {
        setMenuIsOpen(true)
    }

    const handleMenuClose = () => {
        setMenuIsOpen(false)
    }

    const handleBlur = () => {
        // When input loses focus and no selection was made, load default list
        const hasSearchTerm = inputValue && inputValue.trim().length > 0

        if (hasSearchTerm && !hasSelected) {
            // Load default list without search term
            setInputValue('')
            setPage(1)
            setHasSelected(false)
        }
    }

    const isSsoSamlLogin = isSsoSaml(session.loginProvider)

    return (
        <div className="py-2 px-5">
            <Input
                name="email"
                rules={{
                    required: resource.Required,
                    validate: value => validateEmail(value, resource.Email),
                }}
                defaultValue={punchoutSession.email}
                label={`${resource.EmailAddress} *`}
                placeholder={resource.EmailAddressSample}
                formGroup
                formClassName="mb-2"
                readOnly={isSsoSamlLogin || disabled || punchoutSession.disableUnknownUserPopupEmail}
            />
            <Input
                name="firstName"
                rules={{ required: resource.Required }}
                label={`${resource.FirstName} *`}
                defaultValue={punchoutSession.firstName}
                formGroup
                formClassName="mb-2"
                readOnly={isSsoSamlLogin || disabled}
            />
            <Input
                name="lastName"
                rules={{ required: resource.Required }}
                defaultValue={punchoutSession.lastName}
                label={`${resource.LastName} *`}
                formGroup
                formClassName="mb-2"
                readOnly={isSsoSamlLogin || disabled}
            />
            <ReactSelect
                id={`$user.clientSite`}
                isDisabled={disabled}
                isSearchable={true}
                name="site"
                rules={{ required: resource.Required }}
                label={`${resource.Site} *`}
                placeholder={resource.ChooseACompanySite}
                defaultValue={defaultSite}
                options={sites}
                isLoading={loadingMore}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onBlur={handleBlur}
                menuIsOpen={menuIsOpen}
                filterOption={null}
                optionValueKey="clientSiteId"
                optionLabelKey="name"
                formGroup
                formClassName="mb-2"
                components={{
                    Option: ({ data, innerRef, ...innerProps }) => {
                        if (data.isPlaceholder) {
                            return <PlaceholderOption ref={innerRef} label={data.label} />
                        }

                        if (data.isLoadMore) {
                            return (
                                <ShowMoreButton
                                    resource={resource}
                                    disabled={loadingMore}
                                    innerProps={innerProps}
                                    setPage={setPage}
                                />
                            )
                        }
                        const address = data.address || {}
                        return (
                            <components.Option data={data} {...innerProps}>
                                <strong className="d-block">{data.name}</strong>
                                <small className="d-block muted address-mini">{`${address.street || ''} ${address.number || ''} ${address.box || ''
                                    }, ${address.postalCode || ''} ${address.city || ''}, ${address.country ? address.country.name : ''}`}</small>
                            </components.Option>
                        )
                    },
                }}
            />
            <Input
                name="telephone"
                label={resource.PhoneNumber}
                defaultValue={punchoutSession.phoneNumber}
                placeholder={resource.PhoneNumberSample}
                formGroup
                formClassName="mb-2"
                readOnly={isSsoSamlLogin || disabled}
            />
            {punchoutSession.costCenter && (
                <React.Fragment>
                    <Input
                        type="hidden"
                        name="costCenterId"
                        label={resource.CostCenter}
                        defaultValue={punchoutSession.costCenter.id}
                    />
                    <NormalInput
                        name="costCenter"
                        label={resource.CostCenter}
                        defaultValue={punchoutSession.costCenter.costCenter}
                        formGroup
                        formClassName="mb-2"
                        readOnly
                    />
                </React.Fragment>
            )}
            {session.loginProvider === LOGIN_PROVIDER.BELFIUS && (
                <Input type="hidden" name="uiLanguageSetting" defaultValue={session.uiLanguageSetting} />
            )}
            {session.loginProvider === LOGIN_PROVIDER.GOED && (
                <React.Fragment>
                    <NormalInput
                        label={resource.UserGroup}
                        defaultValue={punchoutSession.userGroupCode}
                        formGroup
                        formClassName="mb-2"
                        readOnly
                    />
                    <NormalInput
                        label={resource.NPlusOne}
                        defaultValue={punchoutSession.nPlusOneName}
                        formGroup
                        formClassName="mb-2"
                        readOnly
                    />
                    <Input type="hidden" name="userGroup" defaultValue={punchoutSession.UserGroupId} />
                    <Input type="hidden" name="nplusOneId" defaultValue={punchoutSession.nplusOneId} />
                    <Input type="hidden" name="uiLanguageSetting" defaultValue={session.uiLanguageSetting} />
                </React.Fragment>
            )}
        <ReactSelect
         name="language"
         rules={{ required: resource.Required }}
         label={resource.LanguageLabel} 
         options={languages}
         formGroup 
         formClassName="mb-0"
         isDisabled={disabled}
       />
        </div>
    )
}
